/**
 * src/lib/inflightLetterRecovery.js — RC 1.49.5 L-2.
 *
 * Mirror of inflightCvRecovery.js for cover letters. Closes the
 * remount-rebind UX gap so a wizard remount during letter generation
 * surfaces the recovered letter automatically rather than orphaning
 * the artefact in Documents → Cover Letters.
 *
 * Strategy:
 *   • At generation start (`generateLetter()` in
 *     JobApplicationWizard.jsx), write a sessionStorage marker keyed
 *     by `(userId, jobId)` carrying the deterministic title stamp.
 *   • On wizard remount, if the marker exists AND the restored
 *     wizard state has no `letterId`, GET `/api/cover-letters` and
 *     find the row whose `title` matches the marker. On match,
 *     fetch `/api/cover-letters/{id}` to retrieve the body, and
 *     hydrate the wizard's `letterId` / `letterDraft` / `letterTitle`.
 *
 * Hard rails:
 *   • No new endpoint. No schema change. No billing change.
 *   • Exact title match — no "freshest row" heuristic.
 *   • Cleared on success so a retry never reuses a stale marker.
 *   • TTL aligned to 10 min (matches CV marker + idempotency key).
 */

import { apiFetch } from './api';

export const INFLIGHT_LETTER_RECOVERY_WINDOW_MS = 10 * 60 * 1000;

const KEY_PREFIX = 'inflight-letter';

function _key(userId, jobId) {
  return `${KEY_PREFIX}:${userId || ''}:${jobId || ''}`;
}

export function writeInflightLetterMarker(userId, jobId, title, extras = {}) {
  if (!jobId || !title) return;
  try {
    sessionStorage.setItem(
      _key(userId, jobId),
      JSON.stringify({
        submittedAtMs: Date.now(),
        title,
        // CWS-2 (operator-locked, 2026-02-15) — `runId` is the
        // letter document id once the dispatch returns it. Optional
        // at write time (the 202 dispatch may stamp it via
        // `patchInflightLetterMarker` later). Recovery prefers
        // id-driven `pollCoverLetterRun(runId)` when present.
        runId: extras?.runId || null,
        startedAt: new Date().toISOString(),
      }),
    );
  } catch { /* sessionStorage unavailable — non-fatal */ }
}

// CWS-2 (operator-locked, 2026-02-15) — patch an existing marker
// with newly-known fields (typically `runId` once the helper's 202
// envelope returns it). Idempotent: if the marker doesn't exist
// yet, a new one is written. Failure is silent (private mode, quota).
export function patchInflightLetterMarker(userId, jobId, patch) {
  if (!jobId || !patch || typeof patch !== 'object') return;
  try {
    const raw = sessionStorage.getItem(_key(userId, jobId));
    const existing = raw ? JSON.parse(raw) : null;
    const next = {
      ...(existing && typeof existing === 'object' ? existing : {}),
      ...patch,
    };
    if (!next.submittedAtMs) next.submittedAtMs = Date.now();
    if (!next.startedAt) next.startedAt = new Date().toISOString();
    sessionStorage.setItem(_key(userId, jobId), JSON.stringify(next));
  } catch { /* noop */ }
}

export function clearInflightLetterMarker(userId, jobId) {
  if (!jobId) return;
  try { sessionStorage.removeItem(_key(userId, jobId)); } catch { /* */ }
}

export function readInflightLetterMarker(userId, jobId) {
  if (!jobId) return null;
  try {
    const raw = sessionStorage.getItem(_key(userId, jobId));
    if (!raw) return null;
    const payload = JSON.parse(raw);
    if (!payload || typeof payload !== 'object') return null;
    if (!payload.title || !payload.submittedAtMs) return null;
    return payload;
  } catch { return null; }
}

export function isInflightLetterMarkerExpired(marker, nowMs = Date.now()) {
  if (!marker?.submittedAtMs) return true;
  return (nowMs - Number(marker.submittedAtMs)) > INFLIGHT_LETTER_RECOVERY_WINDOW_MS;
}

/**
 * Look for a cover-letter row whose title EXACTLY matches the marker
 * stamp, then fetch the full body via GET /cover-letters/{id}.
 *
 * @returns {Promise<object|null>} { id, title, content_markdown,
 *   created_at, ... } or null on no match / error.
 */
export async function findLetterByTitleStamp(title) {
  if (!title) return null;
  try {
    const rows = await apiFetch('/api/cover-letters');
    if (!Array.isArray(rows) || rows.length === 0) return null;
    const match = rows.find(
      (r) => r && (r.title || '').trim() === title.trim(),
    );
    if (!match) return null;
    // The list endpoint omits `content_markdown` for payload size;
    // fetch the detail row to recover the body.
    try {
      const detail = await apiFetch(`/api/cover-letters/${match.id}`);
      return detail || match;
    } catch {
      return match;
    }
  } catch { return null; }
}
